/**
 * HTTP contract between the API and its three clients (portal, mobile app,
 * kiosk). Every route the server exposes is typed here and wrapped by
 * ApiClient, so a breaking change shows up as a type error in every client.
 */
import type {
  BreakKind,
  CorrectionStatus,
  DeviceKind,
  DeviceStatus,
  EmployeeStatus,
  EmploymentType,
  EventSource,
  EventStatus,
  EventType,
  ExceptionSeverity,
  ExceptionStatus,
  ExceptionType,
  ExportMethod,
  PayPeriodType,
  PeriodStatus,
  PunchState,
  ReasonCode,
  Role,
  TimesheetStatus,
  AuState,
} from './domain';
import type { RuleConfig, RuleOverrides } from './rules';

/* ------------------------------------------------------------------ DTOs */

export interface RoleAssignmentDTO {
  role: Role;
  /** Teams a supervisor may see. null = all teams (always null above supervisor). */
  teamIds: string[] | null;
}
export interface UserDTO {
  id: string;
  email: string;
  name: string;
  employeeId: string | null;
  roles: RoleAssignmentDTO[];
  status: 'active' | 'disabled';
  mfaEnabled: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}
export interface SessionDTO {
  user: UserDTO;
  /** Highest role held. */
  role: Role;
  /** The session may only call MFA setup routes until this is false. */
  mfaSetupRequired: boolean;
  /** Signed in with a temporary password: prompt for a new one. */
  mustChangePassword: boolean;
  features: { jobCosting: boolean };
  businessName: string;
}
export interface LoginResponse {
  /** Present when no second factor is pending. */
  token?: string;
  /** Present when the user must submit a TOTP code to /auth/mfa. */
  mfaToken?: string;
  mfaRequired: boolean;
  mfaSetupRequired: boolean;
}

export interface TeamDTO {
  id: string;
  name: string;
  supervisorUserIds: string[];
  employeeCount: number;
}
export interface SiteDTO {
  id: string;
  name: string;
  kind: 'factory' | 'install' | 'supplier' | 'other';
  state: AuState;
  timezone: string;
  lat: number;
  lng: number;
  radiusM: number;
  active: boolean;
}
export interface EmployeeDTO {
  id: string;
  employeeNumber: string;
  firstName: string;
  lastName: string;
  preferredName: string | null;
  displayName: string;
  email: string | null;
  mobile: string | null;
  employmentType: EmploymentType;
  startDate: string;
  endDate: string | null;
  teamId: string | null;
  teamName: string | null;
  primarySiteId: string | null;
  supervisorUserId: string | null;
  status: EmployeeStatus;
  myobUid: string | null;
  myobCardId: string | null;
  hasPin: boolean;
  kioskEnabled: boolean;
  privacyAckVersion: number | null;
  categoryOverrides: Record<string, { myobUid: string; myobName: string }>;
  createdAt: string;
}
export interface EmployeeInput {
  employeeNumber: string;
  firstName: string;
  lastName: string;
  preferredName?: string | null;
  email?: string | null;
  mobile?: string | null;
  employmentType: EmploymentType;
  startDate: string;
  endDate?: string | null;
  teamId?: string | null;
  primarySiteId?: string | null;
  supervisorUserId?: string | null;
  status?: EmployeeStatus;
  myobUid?: string | null;
  myobCardId?: string | null;
  kioskEnabled?: boolean;
  categoryOverrides?: Record<string, { myobUid: string; myobName: string }>;
}
export interface DeviceDTO {
  id: string;
  kind: DeviceKind;
  name: string;
  platform: string | null;
  appVersion: string | null;
  employeeId: string | null;
  employeeName: string | null;
  siteId: string | null;
  siteName: string | null;
  status: DeviceStatus;
  lastSeenAt: string | null;
  queueDepth: number | null;
  battery: number | null;
  clockSkewSeconds: number | null;
  enrolledAt: string;
}
export interface EnrolmentCodeDTO {
  code: string;
  /** Null: the code never expires (it still works only once). */
  expiresAt: string | null;
}

export interface RuleTemplateVersionDTO {
  id: string;
  templateId: string;
  versionNumber: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  config: RuleConfig;
  createdAt: string;
  createdByName: string | null;
}
export interface RuleTemplateDTO {
  id: string;
  name: string;
  description: string | null;
  versions: RuleTemplateVersionDTO[];
  assignedEmployeeCount: number;
}
export interface RuleAssignmentDTO {
  id: string;
  employeeId: string;
  templateId: string;
  templateName: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  overrides: RuleOverrides | null;
}
export interface RuleTestShiftInput {
  date: string;
  start: string; // HH:MM local
  end: string; // HH:MM local, may be earlier than start for overnight
  breaks?: { start: string; end: string; kind: BreakKind }[];
  siteKind?: string;
}
export interface RuleTestInput {
  config: RuleConfig;
  timezone: string;
  shifts: RuleTestShiftInput[];
  publicHolidays?: string[];
  roster?: { date: string; start: string; end: string }[];
}

export interface ShiftTemplateDTO {
  id: string;
  name: string;
  startTime: string;
  endTime: string | null;
  breakMinutes: number;
  siteId: string | null;
}
export interface RosterShiftDTO {
  id: string;
  employeeId: string;
  siteId: string | null;
  startAt: string;
  endAt: string | null;
  breakMinutes: number;
  note: string | null;
  status: 'draft' | 'published';
  jobId: string | null;
  updatedAt: string;
}
export interface RosterShiftInput {
  employeeId: string;
  siteId?: string | null;
  startAt: string;
  endAt?: string | null;
  breakMinutes?: number;
  note?: string | null;
  jobId?: string | null;
}

export interface EventDTO {
  id: string;
  employeeId: string;
  type: EventType;
  occurredAt: string;
  receivedAt: string;
  source: EventSource;
  deviceId: string | null;
  appVersion: string | null;
  lat: number | null;
  lng: number | null;
  accuracyM: number | null;
  siteId: string | null;
  siteTz: string;
  breakKind: BreakKind | null;
  jobId: string | null;
  hasPhoto: boolean;
  supersedesId: string | null;
  status: EventStatus;
  reasonCode: ReasonCode | null;
  note: string | null;
  createdByUserId: string | null;
  createdByName: string | null;
  clockSkewSeconds: number | null;
  correctionId: string | null;
}

export type CorrectionChange =
  | { action: 'add'; type: EventType; occurredAt: string; breakKind?: BreakKind | null; jobId?: string | null; siteId?: string | null }
  | { action: 'change'; eventId: string; occurredAt: string }
  | { action: 'void'; eventId: string };

export interface CorrectionDTO {
  id: string;
  employeeId: string;
  employeeName: string;
  requestedByUserId: string | null;
  requestedByName: string;
  requestedByEmployee: boolean;
  shiftDate: string;
  changes: CorrectionChange[];
  reasonCode: ReasonCode;
  note: string | null;
  status: CorrectionStatus;
  decidedByUserId: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
  /** Approved against a locked/exported period: landed as a next-period adjustment. */
  appliedAsAdjustment: boolean;
}
export interface CorrectionInput {
  employeeId: string;
  shiftDate: string;
  changes: CorrectionChange[];
  reasonCode: ReasonCode;
  note?: string | null;
}

export interface ExceptionDTO {
  id: string;
  employeeId: string;
  employeeName: string;
  timesheetId: string | null;
  payPeriodId: string | null;
  type: ExceptionType;
  severity: ExceptionSeverity;
  date: string;
  eventIds: string[];
  rosterShiftId: string | null;
  message: string;
  status: ExceptionStatus;
  resolvedByName: string | null;
  resolvedAt: string | null;
  resolutionNote: string | null;
  createdAt: string;
  /** Days until the period's approval deadline (negative = overdue). */
  deadlineDays: number | null;
}

export interface PeriodCounts {
  total: number;
  open: number;
  ready: number;
  needs_attention: number;
  approved: number;
  rejected: number;
  locked: number;
  exported: number;
}
export interface PayPeriodDTO {
  id: string;
  startDate: string;
  endDate: string;
  status: PeriodStatus;
  lockedAt: string | null;
  lockedByName: string | null;
  approvalDeadline: string;
  counts: PeriodCounts;
  isCurrent: boolean;
}
export interface TimesheetTotals {
  workedHours: number;
  paidHours: number;
  byCategory: Record<string, number>;
}
export interface TimesheetSummaryDTO {
  id: string;
  payPeriodId: string;
  employeeId: string;
  employeeName: string;
  employeeNumber: string;
  teamId: string | null;
  status: TimesheetStatus;
  totals: TimesheetTotals;
  openBlocking: number;
  openReview: number;
  approvedByName: string | null;
  approvedAt: string | null;
  calcVersion: number;
}
export interface TimesheetLineDTO {
  id: string;
  date: string;
  category: string;
  hours: number;
  unit: 'hours' | 'units';
  ruleRef: string;
  sourceEventIds: string[];
  jobId: string | null;
  isAdjustment: boolean;
  adjustmentForDate: string | null;
  exportBatchId: string | null;
}
export interface ShiftDTO {
  date: string;
  startAt: string;
  endAt: string | null;
  siteId: string | null;
  tz: string;
  breaks: { startAt: string; endAt: string | null; kind: BreakKind; paid: boolean }[];
  workedMinutes: number;
  paidMinutes: number;
  eventIds: string[];
  status: 'complete' | 'in_progress' | 'broken';
  rosterShiftId: string | null;
}
export interface LineDiffDTO {
  date: string;
  category: string;
  jobId: string | null;
  before: number;
  after: number;
}
export interface TimesheetDetailDTO extends TimesheetSummaryDTO {
  period: PayPeriodDTO;
  lines: TimesheetLineDTO[];
  shifts: ShiftDTO[];
  exceptions: ExceptionDTO[];
  events: EventDTO[];
  roster: RosterShiftDTO[];
  diff: LineDiffDTO[] | null;
  rejectedNote: string | null;
  timezone: string;
}

export interface CategoryDTO {
  code: string;
  name: string;
  unit: 'hours' | 'units';
  myobUid: string | null;
  myobName: string | null;
  /** Paid outside MYOB (e.g. cash): left out of the export, listed on the cash hours report. */
  paidInCash: boolean;
}

export interface ExportProblem {
  kind: 'period_not_locked' | 'employee_unmatched' | 'category_unmapped' | 'already_exported' | 'myob_not_connected' | 'job_unmapped';
  message: string;
  employeeId?: string;
  category?: string;
}
export interface ExportPreflightDTO {
  ok: boolean;
  problems: ExportProblem[];
}
export interface ExportRow {
  employeeId: string;
  employeeName: string;
  employeeNumber: string;
  myobUid: string | null;
  myobCardId: string | null;
  weekStart: string;
  date: string;
  category: string;
  myobCategoryUid: string | null;
  myobCategoryName: string | null;
  jobId: string | null;
  jobCode: string | null;
  myobJobUid: string | null;
  hours: number;
  note: string | null;
}
export interface ExportPreviewDTO {
  periodId: string;
  weeks: { weekStart: string; weekEnd: string }[];
  rows: ExportRow[];
  totalsByEmployee: { employeeId: string; employeeName: string; hours: number; previousHours: number | null }[];
  totalsByCategory: Record<string, number>;
  /** Hours in paid-in-cash categories, left out of the export. */
  cashHours: Record<string, number>;
  contentHash: string;
}
export interface ReconciliationEmployee {
  employeeId: string;
  employeeName: string;
  lines: number;
  accepted: number;
  rejected: number;
  hours: number;
  errors: string[];
}
export interface ReconciliationReport {
  periodStart: string;
  periodEnd: string;
  method: ExportMethod;
  linesSent: number;
  linesAccepted: number;
  linesRejected: number;
  byEmployee: ReconciliationEmployee[];
  byCategory: Record<string, number>;
  checklist: string[];
}
export interface ExportBatchDTO {
  id: string;
  payPeriodId: string;
  method: ExportMethod;
  status: 'pending' | 'succeeded' | 'partial' | 'failed';
  contentHash: string;
  createdByName: string;
  createdAt: string;
  reason: string | null;
  previousBatchId: string | null;
  report: ReconciliationReport | null;
  files: string[];
}

export interface LiveBoardDTO {
  now: string;
  onSite: {
    employeeId: string;
    employeeName: string;
    state: PunchState;
    since: string;
    siteId: string | null;
    siteName: string | null;
    source: EventSource;
    outsideGeofence: boolean;
    jobCode: string | null;
  }[];
  absent: { employeeId: string; employeeName: string; rosterStart: string; siteName: string | null; minutesLate: number }[];
  lateArrivals: { employeeId: string; employeeName: string; rosterStart: string; clockIn: string; minutesLate: number }[];
}

export interface SettingsDTO {
  businessName: string;
  payPeriodType: PayPeriodType;
  periodAnchorDate: string;
  approvalDeadlineHours: number;
  defaultState: AuState;
  defaultTimezone: string;
  photoCaptureEnabled: boolean;
  photoRetentionDays: number;
  missedPunchReminderMinutes: number;
  kioskOperatingHours: { start: string; end: string };
  employeeTimesheetAck: boolean;
  retentionYears: number;
  alertEmail: string | null;
  features: { jobCosting: boolean };
  exportMethod: ExportMethod;
}
export interface MyobStatusDTO {
  configured: boolean;
  connected: boolean;
  companyFileName: string | null;
  companyFileUri: string | null;
  lastTestAt: string | null;
  lastTestOk: boolean | null;
  lastTestMessage: string | null;
}
export interface MyobEmployeeDTO {
  uid: string;
  displayId: string | null;
  firstName: string | null;
  lastName: string | null;
  isActive: boolean;
}
export interface MyobCategoryDTO {
  uid: string;
  name: string;
  type: string | null;
}
export interface MyobJobDTO {
  uid: string;
  number: string;
  name: string;
}

export interface PublicHolidayDTO {
  id: string;
  state: AuState;
  date: string;
  name: string;
}
export interface PrivacyNoticeDTO {
  version: number;
  title: string;
  body: string;
  publishedAt: string;
}
export interface AuditEntryDTO {
  id: string;
  at: string;
  actorType: 'user' | 'device' | 'employee' | 'system';
  actorId: string | null;
  actorName: string | null;
  action: string;
  objectType: string;
  objectId: string | null;
  before: unknown;
  after: unknown;
  ip: string | null;
  deviceId: string | null;
}
export interface JobDTO {
  id: string;
  code: string;
  name: string;
  customer: string | null;
  siteId: string | null;
  status: 'active' | 'complete' | 'on_hold';
  budgetHours: number | null;
  myobJobUid: string | null;
  createdAt: string;
}
export interface JobInput {
  code: string;
  name: string;
  customer?: string | null;
  siteId?: string | null;
  status?: 'active' | 'complete' | 'on_hold';
  budgetHours?: number | null;
  myobJobUid?: string | null;
}
export interface NotificationDTO {
  id: string;
  kind: string;
  title: string;
  body: string;
  data: Record<string, unknown> | null;
  createdAt: string;
  readAt: string | null;
}

/* Reports */
export interface HoursReportRow {
  employeeId: string;
  employeeName: string;
  byCategory: Record<string, number>;
  workedHours: number;
}
export interface CashReportRow {
  employeeId: string;
  employeeName: string;
  employeeNumber: string;
  totalHours: number;
  days: { date: string; category: string; categoryName: string; hours: number }[];
}
export interface ExceptionReportRow {
  type: ExceptionType;
  open: number;
  resolved: number;
  dismissed: number;
}
export interface RosterVsActualRow {
  employeeId: string;
  employeeName: string;
  rosteredHours: number;
  actualHours: number;
  variance: number;
  lateStarts: number;
  earlyFinishes: number;
  absences: number;
}
export interface PhotoReportRow {
  eventId: string;
  employeeId: string;
  employeeName: string;
  type: EventType;
  occurredAt: string;
  siteName: string | null;
  photoUrl: string;
}
export interface JobReportRow {
  jobId: string | null;
  jobCode: string | null;
  jobName: string;
  hours: number;
  budgetHours: number | null;
  byEmployee: { employeeId: string; employeeName: string; hours: number }[];
}

/* ---------------------------------------------------------- Mobile/kiosk */

export interface GeofenceDTO {
  siteId: string;
  name: string;
  lat: number;
  lng: number;
  radiusM: number;
}
export interface MeDTO {
  employee: { id: string; displayName: string; employeeNumber: string; employmentType: EmploymentType };
  state: PunchState;
  /** Start of the current shift while in or on a break; last clock-out when out. */
  since: string | null;
  /** Newest active punch the server holds, used to reconcile offline punches. */
  lastEventAt: string | null;
  currentJobId: string | null;
  sites: GeofenceDTO[];
  privacy: { currentVersion: number | null; acknowledgedVersion: number | null; notice: PrivacyNoticeDTO | null };
  settings: { breakKinds: BreakKind[]; jobCosting: boolean; missedPunchReminderMinutes: number };
  device: { id: string; status: DeviceStatus };
  /** IANA zone used to show this employee's times. */
  timezone: string;
  serverTime: string;
}
export interface PunchInput {
  /** Client-generated UUID; a punch synced twice is stored once. */
  idempotencyKey: string;
  type: EventType;
  occurredAt: string;
  lat?: number | null;
  lng?: number | null;
  accuracyM?: number | null;
  breakKind?: BreakKind | null;
  jobId?: string | null;
}
export interface KioskPunchInput extends PunchInput {
  employeeId: string;
  /** JPEG, base64 without data: prefix. Optional. */
  photoBase64?: string | null;
}
export interface PunchBatchInput<T extends PunchInput = PunchInput> {
  /** Device clock at the moment of sending, used to detect clock skew. */
  deviceTime: string;
  punches: T[];
}
export interface PunchResult {
  idempotencyKey: string;
  status: 'stored' | 'duplicate' | 'rejected';
  eventId?: string;
  siteId?: string | null;
  siteName?: string | null;
  outsideGeofence?: boolean;
  error?: string;
}
export interface PunchBatchResponse {
  results: PunchResult[];
  serverTime: string;
}
export interface MyTimesheetDTO {
  period: { id: string; startDate: string; endDate: string };
  status: TimesheetStatus;
  totals: TimesheetTotals;
  lines: TimesheetLineDTO[];
  shifts: ShiftDTO[];
  exceptions: { type: ExceptionType; severity: ExceptionSeverity; date: string; message: string; status: ExceptionStatus }[];
  timezone: string;
}
export interface KioskEmployeeDTO {
  id: string;
  displayName: string;
  /** sha256(pinSalt + PIN), hex. Lets the kiosk identify staff while offline. */
  pinLookup: string;
  state: PunchState;
  since: string | null;
  lastEventAt: string | null;
  isSupervisor: boolean;
}
export interface KioskBootstrapDTO {
  device: { id: string; name: string };
  site: { id: string; name: string; timezone: string };
  pinSalt: string;
  employees: KioskEmployeeDTO[];
  jobs: { id: string; code: string; name: string }[];
  settings: { photoCaptureEnabled: boolean; operatingHours: { start: string; end: string }; jobCosting: boolean; breakKinds: BreakKind[] };
  serverTime: string;
}
export interface EnrolInput {
  code: string;
  kind: DeviceKind;
  platform: string;
  name: string;
  appVersion: string;
}
/** Phone app sign-in: mobile number + PIN. */
export interface DeviceLoginInput {
  mobile: string;
  pin: string;
  platform: string;
  name: string;
  appVersion: string;
}
/** Saving an employee may set their PIN to the last 4 digits of their mobile. */
export type EmployeeSaveResult = EmployeeDTO & { defaultPin?: 'set' | 'taken' | 'no_mobile' };
export interface EnrolResponse {
  token: string;
  deviceId: string;
  kind: DeviceKind;
  status: DeviceStatus;
  employeeName: string | null;
  siteName: string | null;
}
export interface MyCorrectionInput {
  shiftDate: string;
  changes: CorrectionChange[];
  reasonCode: ReasonCode;
  note?: string | null;
}

/* ---------------------------------------------------------------- Client */

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

type Query = Record<string, string | number | boolean | null | undefined>;

export interface ApiClientOptions {
  baseUrl: string;
  getToken: () => string | null | Promise<string | null>;
  onUnauthorized?: () => void;
  fetchImpl?: typeof fetch;
}

export class ApiClient {
  constructor(private opts: ApiClientOptions) {}

  async request<T>(method: string, path: string, body?: unknown, query?: Query): Promise<T> {
    const qs = query
      ? '?' +
        Object.entries(query)
          .filter(([, v]) => v !== undefined && v !== null && v !== '')
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join('&')
      : '';
    const token = await this.opts.getToken();
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (token) headers.authorization = `Bearer ${token}`;
    const f = this.opts.fetchImpl ?? fetch;
    const res = await f(`${this.opts.baseUrl}${path}${qs === '?' ? '' : qs}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (res.status === 401) this.opts.onUnauthorized?.();
    const text = await res.text();
    let data: any = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      // A proxy or gateway error page is not JSON; still report it as an ApiError.
      throw new ApiError(res.status, res.ok ? 'bad_response' : 'unavailable', res.ok ? 'The server sent an unexpected response' : `The server is not available (${res.status}). Try again in a moment.`);
    }
    if (!res.ok) {
      throw new ApiError(res.status, data?.code ?? 'error', data?.message ?? res.statusText, data?.details);
    }
    return data as T;
  }
  get<T>(path: string, query?: Query) {
    return this.request<T>('GET', path, undefined, query);
  }
  post<T>(path: string, body: unknown = {}) {
    return this.request<T>('POST', path, body);
  }
  patch<T>(path: string, body: unknown) {
    return this.request<T>('PATCH', path, body);
  }
  put<T>(path: string, body: unknown) {
    return this.request<T>('PUT', path, body);
  }
  del<T>(path: string) {
    return this.request<T>('DELETE', path);
  }
  /**
   * URL for a binary download opened with <a href> or <img src>. The server
   * signs a link valid for two minutes for this exact path, so the session
   * token never appears in a URL, a log or browser history.
   */
  async downloadUrl(path: string): Promise<string> {
    const { token } = await this.post<{ token: string }>('/api/auth/download-token', { path: path.split('?')[0] });
    return `${this.opts.baseUrl}${path}${path.includes('?') ? '&' : '?'}access_token=${encodeURIComponent(token)}`;
  }

  /* auth */
  login = (email: string, password: string) => this.post<LoginResponse>('/api/auth/login', { email, password });
  mfa = (mfaToken: string, code: string) => this.post<{ token: string }>('/api/auth/mfa', { mfaToken, code });
  totpSetup = () => this.post<{ secret: string; otpauthUrl: string }>('/api/auth/totp/setup');
  totpEnable = (code: string) => this.post<{ ok: true }>('/api/auth/totp/enable', { code });
  session = () => this.get<SessionDTO>('/api/auth/session');
  logout = () => this.post<{ ok: true }>('/api/auth/logout');
  changePassword = (current: string, next: string) => this.post<{ ok: true }>('/api/auth/password', { current, next });

  /* users (list: payroll admin and owner; changes: owner) */
  users = () => this.get<UserDTO[]>('/api/users');
  createUser = (b: { email: string; name: string; roles: RoleAssignmentDTO[]; employeeId?: string | null }) =>
    this.post<{ user: UserDTO; temporaryPassword: string }>('/api/users', b);
  updateUser = (id: string, b: Partial<{ name: string; roles: RoleAssignmentDTO[]; employeeId: string | null; status: 'active' | 'disabled' }>) =>
    this.patch<UserDTO>(`/api/users/${id}`, b);
  resetUserMfa = (id: string) => this.post<{ ok: true }>(`/api/users/${id}/reset-mfa`);
  resetUserPassword = (id: string) => this.post<{ temporaryPassword: string }>(`/api/users/${id}/reset-password`);

  /* notifications for portal users (deadline reminders, rerouted approvals, kiosk lockouts, sync health) */
  userNotifications = () => this.get<NotificationDTO[]>('/api/notifications');
  readUserNotification = (id: string) => this.post<{ ok: true }>(`/api/notifications/${id}/read`);
  readAllUserNotifications = () => this.post<{ ok: true }>('/api/notifications/read-all');

  /* teams and sites */
  teams = () => this.get<TeamDTO[]>('/api/teams');
  createTeam = (b: { name: string; supervisorUserIds: string[] }) => this.post<TeamDTO>('/api/teams', b);
  updateTeam = (id: string, b: Partial<{ name: string; supervisorUserIds: string[] }>) => this.patch<TeamDTO>(`/api/teams/${id}`, b);
  sites = () => this.get<SiteDTO[]>('/api/sites');
  createSite = (b: Omit<SiteDTO, 'id' | 'active'> & { active?: boolean }) => this.post<SiteDTO>('/api/sites', b);
  updateSite = (id: string, b: Partial<Omit<SiteDTO, 'id'>>) => this.patch<SiteDTO>(`/api/sites/${id}`, b);

  /* employees */
  employees = (q?: { status?: EmployeeStatus | 'all'; teamId?: string; q?: string }) => this.get<EmployeeDTO[]>('/api/employees', q);
  employee = (id: string) => this.get<EmployeeDTO>(`/api/employees/${id}`);
  createEmployee = (b: EmployeeInput) => this.post<EmployeeSaveResult>('/api/employees', b);
  updateEmployee = (id: string, b: Partial<EmployeeInput>) => this.patch<EmployeeSaveResult>(`/api/employees/${id}`, b);
  setEmployeePin = (id: string, pin: string) => this.post<{ ok: true }>(`/api/employees/${id}/pin`, { pin });
  employeeDevices = (id: string) => this.get<DeviceDTO[]>(`/api/employees/${id}/devices`);
  employeeTimeline = (id: string, q?: { from?: string; to?: string }) => this.get<AuditEntryDTO[]>(`/api/employees/${id}/timeline`, q);
  ruleAssignments = (employeeId: string) => this.get<RuleAssignmentDTO[]>(`/api/employees/${employeeId}/rule-assignments`);
  createRuleAssignment = (employeeId: string, b: { templateId: string; effectiveFrom: string; effectiveTo?: string | null; overrides?: RuleOverrides | null }) =>
    this.post<RuleAssignmentDTO>(`/api/employees/${employeeId}/rule-assignments`, b);
  updateRuleAssignment = (id: string, b: Partial<{ effectiveTo: string | null; overrides: RuleOverrides | null }>) =>
    this.patch<RuleAssignmentDTO>(`/api/rule-assignments/${id}`, b);

  /* devices */
  devices = (q?: { kind?: DeviceKind }) => this.get<DeviceDTO[]>('/api/devices', q);
  approveDevice = (id: string) => this.post<DeviceDTO>(`/api/devices/${id}/approve`);
  revokeDevice = (id: string) => this.post<DeviceDTO>(`/api/devices/${id}/revoke`);
  kioskEnrolmentCode = (b: { siteId: string; name: string }) => this.post<EnrolmentCodeDTO>('/api/kiosks/enrolment-code', b);

  /* rules */
  ruleTemplates = () => this.get<RuleTemplateDTO[]>('/api/rule-templates');
  createRuleTemplate = (b: { name: string; description?: string | null; config: RuleConfig; effectiveFrom: string }) =>
    this.post<RuleTemplateDTO>('/api/rule-templates', b);
  updateRuleTemplate = (id: string, b: { name?: string; description?: string | null }) => this.patch<RuleTemplateDTO>(`/api/rule-templates/${id}`, b);
  createRuleVersion = (templateId: string, b: { config: RuleConfig; effectiveFrom: string }) =>
    this.post<RuleTemplateDTO>(`/api/rule-templates/${templateId}/versions`, b);
  testRules = (b: RuleTestInput) => this.post<EngineOutputDTO>('/api/rules/test', b);

  /* rosters */
  shiftTemplates = () => this.get<ShiftTemplateDTO[]>('/api/shift-templates');
  createShiftTemplate = (b: Omit<ShiftTemplateDTO, 'id'>) => this.post<ShiftTemplateDTO>('/api/shift-templates', b);
  updateShiftTemplate = (id: string, b: Partial<Omit<ShiftTemplateDTO, 'id'>>) => this.patch<ShiftTemplateDTO>(`/api/shift-templates/${id}`, b);
  deleteShiftTemplate = (id: string) => this.del<{ ok: true }>(`/api/shift-templates/${id}`);
  roster = (q: { from: string; to: string; teamId?: string }) => this.get<RosterShiftDTO[]>('/api/roster', q);
  createRosterShift = (b: RosterShiftInput) => this.post<RosterShiftDTO>('/api/roster/shifts', b);
  updateRosterShift = (id: string, b: Partial<RosterShiftInput>) => this.patch<RosterShiftDTO>(`/api/roster/shifts/${id}`, b);
  deleteRosterShift = (id: string) => this.del<{ ok: true }>(`/api/roster/shifts/${id}`);
  publishRoster = (b: { from: string; to: string; teamId?: string }) => this.post<{ published: number; notified: number }>('/api/roster/publish', b);
  copyRosterWeek = (b: { fromWeekStart: string; toWeekStart: string; teamId?: string; employeeId?: string }) =>
    this.post<{ created: number }>('/api/roster/copy-week', b);

  /* attendance */
  live = () => this.get<LiveBoardDTO>('/api/live');
  events = (q: { employeeId?: string; from: string; to: string }) => this.get<EventDTO[]>('/api/events', q);
  corrections = (q?: { status?: CorrectionStatus; employeeId?: string }) => this.get<CorrectionDTO[]>('/api/corrections', q);
  createCorrection = (b: CorrectionInput) => this.post<CorrectionDTO>('/api/corrections', b);
  approveCorrection = (id: string, note?: string) => this.post<CorrectionDTO>(`/api/corrections/${id}/approve`, { note });
  rejectCorrection = (id: string, note: string) => this.post<CorrectionDTO>(`/api/corrections/${id}/reject`, { note });
  exceptions = (q?: { status?: ExceptionStatus | 'all'; payPeriodId?: string; type?: ExceptionType; employeeId?: string }) =>
    this.get<ExceptionDTO[]>('/api/exceptions', q);
  dismissException = (id: string, note: string) => this.post<ExceptionDTO>(`/api/exceptions/${id}/dismiss`, { note });

  /* periods and timesheets */
  payPeriods = () => this.get<PayPeriodDTO[]>('/api/pay-periods');
  payPeriod = (id: string) => this.get<{ period: PayPeriodDTO; timesheets: TimesheetSummaryDTO[] }>(`/api/pay-periods/${id}`);
  lockPeriod = (id: string) => this.post<PayPeriodDTO>(`/api/pay-periods/${id}/lock`);
  unlockPeriod = (id: string, reason: string) => this.post<PayPeriodDTO>(`/api/pay-periods/${id}/unlock`, { reason });
  recalculatePeriod = (id: string) => this.post<{ recalculated: number }>(`/api/pay-periods/${id}/recalculate`);
  timesheet = (id: string) => this.get<TimesheetDetailDTO>(`/api/timesheets/${id}`);
  approveTimesheet = (id: string) => this.post<TimesheetSummaryDTO>(`/api/timesheets/${id}/approve`);
  rejectTimesheet = (id: string, note: string) => this.post<TimesheetSummaryDTO>(`/api/timesheets/${id}/reject`, { note });
  withdrawApproval = (id: string) => this.post<TimesheetSummaryDTO>(`/api/timesheets/${id}/withdraw-approval`);
  bulkApprove = (ids: string[]) =>
    this.post<{ approved: string[]; skipped: { id: string; reason: string }[] }>('/api/timesheets/bulk-approve', { ids });
  recalculateTimesheet = (id: string) => this.post<TimesheetSummaryDTO>(`/api/timesheets/${id}/recalculate`);

  /* export */
  exportPreflight = (periodId: string, q?: { method?: ExportMethod; reexport?: boolean }) =>
    this.get<ExportPreflightDTO>(`/api/pay-periods/${periodId}/export/preflight`, q);
  exportPreview = (periodId: string) => this.get<ExportPreviewDTO>(`/api/pay-periods/${periodId}/export/preview`);
  runExport = (periodId: string, b: { method: ExportMethod; reason?: string | null; reexport?: boolean }) =>
    this.post<ExportBatchDTO>(`/api/pay-periods/${periodId}/export`, b);
  exportBatches = (q?: { payPeriodId?: string }) => this.get<ExportBatchDTO[]>('/api/export-batches', q);
  exportBatch = (id: string) => this.get<ExportBatchDTO>(`/api/export-batches/${id}`);

  /* categories and MYOB */
  categories = () => this.get<CategoryDTO[]>('/api/categories');
  upsertCategory = (code: string, b: { name: string; unit: 'hours' | 'units'; myobUid: string | null; myobName: string | null; paidInCash?: boolean }) =>
    this.put<CategoryDTO>(`/api/categories/${code}`, b);
  myobStatus = () => this.get<MyobStatusDTO>('/api/myob/status');
  myobConnectUrl = () => this.get<{ url: string }>('/api/myob/connect-url');
  myobCompanyFiles = () => this.get<{ uri: string; name: string }[]>('/api/myob/company-files');
  myobSelectCompanyFile = (b: { uri: string; name: string; username?: string; password?: string }) => this.post<MyobStatusDTO>('/api/myob/company-file', b);
  myobTest = () => this.post<MyobStatusDTO>('/api/myob/test');
  myobEmployees = () => this.get<MyobEmployeeDTO[]>('/api/myob/employees');
  myobCategories = () => this.get<MyobCategoryDTO[]>('/api/myob/categories');
  myobJobs = () => this.get<MyobJobDTO[]>('/api/myob/jobs');
  myobAutoMatch = () => this.post<{ matched: number; unmatched: string[] }>('/api/myob/auto-match');

  /* reports */
  reportHours = (q: { from: string; to: string }) => this.get<HoursReportRow[]>('/api/reports/hours', q);
  reportExceptions = (q: { from: string; to: string }) => this.get<ExceptionReportRow[]>('/api/reports/exceptions', q);
  reportCash = (q: { from: string; to: string }) => this.get<CashReportRow[]>('/api/reports/cash', q);
  reportRosterVsActual = (q: { from: string; to: string }) => this.get<RosterVsActualRow[]>('/api/reports/roster-vs-actual', q);
  reportPhotos = (q: { from: string; to: string }) => this.get<PhotoReportRow[]>('/api/reports/photos', q);
  reportJobs = (q: { from: string; to: string }) => this.get<JobReportRow[]>('/api/reports/jobs', q);

  /* audit */
  audit = (q?: { objectType?: string; objectId?: string; actorId?: string; from?: string; to?: string; limit?: number; before?: string }) =>
    this.get<AuditEntryDTO[]>('/api/audit', q);

  /* settings */
  settings = () => this.get<SettingsDTO>('/api/settings');
  updateSettings = (b: Partial<SettingsDTO>) => this.put<SettingsDTO>('/api/settings', b);
  publicHolidays = (q?: { state?: AuState; year?: number }) => this.get<PublicHolidayDTO[]>('/api/public-holidays', q);
  createPublicHoliday = (b: { state: AuState; date: string; name: string }) => this.post<PublicHolidayDTO>('/api/public-holidays', b);
  deletePublicHoliday = (id: string) => this.del<{ ok: true }>(`/api/public-holidays/${id}`);
  importPublicHolidays = (b: { state: AuState; year: number }) => this.post<{ created: number }>('/api/public-holidays/import', b);
  privacyNotices = () => this.get<PrivacyNoticeDTO[]>('/api/privacy-notices');
  publishPrivacyNotice = (b: { title: string; body: string }) => this.post<PrivacyNoticeDTO>('/api/privacy-notices', b);

  /* jobs (phase 2) */
  jobs = (q?: { status?: string }) => this.get<JobDTO[]>('/api/jobs', q);
  createJob = (b: JobInput) => this.post<JobDTO>('/api/jobs', b);
  updateJob = (id: string, b: Partial<JobInput>) => this.patch<JobDTO>(`/api/jobs/${id}`, b);

  /* mobile (device token) */
  enrol = (b: EnrolInput) => this.post<EnrolResponse>('/api/device/enrol', b);
  deviceLogin = (b: DeviceLoginInput) => this.post<EnrolResponse>('/api/device/login', b);
  me = () => this.get<MeDTO>('/api/me');
  sendPunches = (b: PunchBatchInput) => this.post<PunchBatchResponse>('/api/me/punches', b);
  myRoster = (q: { from: string; to: string }) => this.get<RosterShiftDTO[]>('/api/me/roster', q);
  myTimesheet = (which: 'current' | 'previous') => this.get<MyTimesheetDTO>('/api/me/timesheet', { which });
  myCorrections = () => this.get<CorrectionDTO[]>('/api/me/corrections');
  requestCorrection = (b: MyCorrectionInput) => this.post<CorrectionDTO>('/api/me/corrections', b);
  myNotifications = () => this.get<NotificationDTO[]>('/api/me/notifications');
  readNotification = (id: string) => this.post<{ ok: true }>(`/api/me/notifications/${id}/read`);
  acknowledgePrivacy = (version: number) => this.post<{ ok: true }>('/api/me/privacy-ack', { version });
  /** VAPID public key for the browser's PushManager.subscribe(). */
  pushConfig = () => this.get<{ publicKey: string }>('/api/me/push-config');
  /** `token` is JSON.stringify(PushSubscription). */
  registerPushToken = (token: string) => this.post<{ ok: true }>('/api/me/push-token', { token });
  changeMyPin = (b: { currentPin: string | null; newPin: string }) => this.post<{ ok: true }>('/api/me/pin', b);
  myJobs = () => this.get<{ id: string; code: string; name: string }[]>('/api/me/jobs');
  /** My own punches (all statuses) between two local dates, for correction requests. */
  myEvents = (q: { from: string; to: string }) => this.get<EventDTO[]>('/api/me/events', q);

  /* kiosk (device token) */
  kioskBootstrap = () => this.get<KioskBootstrapDTO>('/api/kiosk/bootstrap');
  kioskPunches = (b: PunchBatchInput<KioskPunchInput>) => this.post<PunchBatchResponse>('/api/kiosk/punches', b);
  kioskHeartbeat = (b: { battery: number | null; queueDepth: number; appVersion: string; deviceTime: string }) =>
    this.post<{ serverTime: string }>('/api/kiosk/heartbeat', b);
  kioskAudit = (b: { entries: { action: string; at: string; detail?: Record<string, unknown> }[] }) =>
    this.post<{ ok: true }>('/api/kiosk/audit', b);
}

/** Engine output as returned by the rule tester. Mirrors @em/engine's types. */
export interface EngineOutputDTO {
  shifts: ShiftDTO[];
  lines: { date: string; category: string; hours: number; unit: 'hours' | 'units'; ruleRef: string; sourceEventIds: string[]; jobId: string | null }[];
  exceptions: { type: ExceptionType; severity: ExceptionSeverity; date: string; eventIds: string[]; message: string; fingerprint: string; rosterShiftId: string | null }[];
  totals: TimesheetTotals;
}
